/**
 * BlueprintViewer
 *
 * SVG renderer for planned blueprint visual artifacts.
 *
 * Primary responsibilities:
 * - Render rooms, sections, walls, dimensions, and annotations
 * - Present blueprint scale, title, and notes in a compact artifact frame
 *
 * Dependencies:
 * - React
 */

'use client'

import { useId } from 'react'

export type BlueprintPoint = {
  x: number
  y: number
}

export type BlueprintBounds = {
  width: number
  height: number
}

export type BlueprintSection = {
  id: string
  label: string
  kind?: string
  x?: number
  y?: number
  width?: number
  height?: number
  points?: BlueprintPoint[]
  fill?: string
  stroke?: string
  notes?: string
}

export type BlueprintLine = {
  id: string
  label?: string
  kind?: string
  from?: BlueprintPoint
  to?: BlueprintPoint
  start?: BlueprintPoint
  end?: BlueprintPoint
  thickness?: number
}

export type BlueprintDimension = {
  id: string
  label?: string
  from?: BlueprintPoint
  to?: BlueprintPoint
  start?: BlueprintPoint
  end?: BlueprintPoint
  offset?: number
}

export type BlueprintAnnotation = {
  id: string
  label: string
  note?: string
  x: number
  y: number
  target?: BlueprintPoint
}

export type BlueprintScale = {
  label?: string
  ratio?: string
  unit?: string
  length?: number
}

export type BlueprintElementKind =
  | 'wall'
  | 'door'
  | 'window'
  | 'fixture'
  | 'dimension'
  | 'label'
  | 'area'

export type BlueprintElement = {
  id: string
  kind: BlueprintElementKind
  points: BlueprintPoint[]
  label?: string
  measurement?: number
}

export type BlueprintData = {
  bounds?: BlueprintBounds
  scale?: BlueprintScale | string
  unit?: string
  elements?: BlueprintElement[]
  rooms?: BlueprintSection[]
  sections?: BlueprintSection[]
  walls?: BlueprintLine[]
  lines?: BlueprintLine[]
  dimensions?: BlueprintDimension[]
  annotations?: BlueprintAnnotation[]
  notes?: string | string[]
  sourceNotes?: string[]
  confidence?: number
}

export type BlueprintArtifact = {
  id: string
  type: 'blueprint'
  title: string
  createdAt?: string
  data: BlueprintData
}

export type BlueprintViewerProps = {
  artifact?: BlueprintArtifact | null
  className?: string
}

type CanvasBounds = {
  minX: number
  minY: number
  maxX: number
  maxY: number
  width: number
  height: number
}

type LineEndpoints = {
  start: BlueprintPoint
  end: BlueprintPoint
}

type DimensionGeometry = {
  start: BlueprintPoint
  end: BlueprintPoint
  dimensionStart: BlueprintPoint
  dimensionEnd: BlueprintPoint
  labelPoint: BlueprintPoint
  angle: number
}

type PositionedBlueprintAnnotation = BlueprintAnnotation & {
  targetPoint: BlueprintPoint
  labelX: number
  labelY: number
  labelWidth: number
  labelHeight: number
  labelLines: string[]
  noteLine?: string
}

const canvasPadding = 72
const defaultBounds: BlueprintBounds = {
  width: 900,
  height: 560
}

const areaPalette = ['#e0f2fe', '#ecfdf5', '#fef3c7', '#f1f5f9', '#fce7f3']
const blueprintInk = '#0f172a'
const mutedInk = '#64748b'
const backgroundInk = '#f8fafc'
const annotationGap = 32

// --------------

export default function BlueprintViewer({ artifact, className }: BlueprintViewerProps) {
  const reactId = useId().replace(/:/g, '')
  const gridId = `${reactId}-blueprint-grid`
  const dimensionMarkerId = `${reactId}-dimension-marker`

  const wrapperClassName = joinClassNames(
    'not-prose overflow-hidden rounded-lg border border-border bg-background text-foreground shadow-sm',
    className
  )

  if (!artifact) {
    return (
      <section className={wrapperClassName}>
        <BlueprintState title="Nothing to show" message="No blueprint data is available yet." />
      </section>
    )
  }

  const rawSections = getBlueprintSections(artifact.data)
  const rawLines = getBlueprintLines(artifact.data)
  const rawDimensions = getBlueprintDimensions(artifact.data)
  const rawAnnotations = getBlueprintAnnotations(artifact.data)
  const hasRenderableData =
    rawSections.length > 0 ||
    rawLines.length > 0 ||
    rawDimensions.length > 0 ||
    rawAnnotations.length > 0

  if (!hasRenderableData) {
    return (
      <section className={wrapperClassName}>
        <BlueprintState
          title={artifact.title}
          message="Add rooms, walls, dimensions, or annotations to render the blueprint."
        />
      </section>
    )
  }

  const coordinateScale = getBlueprintDisplayScale(
    artifact.data,
    rawSections,
    rawLines,
    rawDimensions,
    rawAnnotations
  )
  const displayData = scaleBlueprintData(artifact.data, coordinateScale)
  const sections = scaleBlueprintSections(rawSections, coordinateScale)
  const lines = scaleBlueprintLines(rawLines, coordinateScale)
  const dimensions = scaleBlueprintDimensions(rawDimensions, coordinateScale)
  const annotations = scaleBlueprintAnnotations(rawAnnotations, coordinateScale)
  const bounds = getCanvasBounds(displayData, sections, lines, dimensions, annotations)
  const positionedAnnotations = positionBlueprintAnnotations(annotations, bounds)
  const unit = artifact.data.unit ?? getScaleUnit(artifact.data.scale)
  const notes = getBlueprintNotes(artifact.data, rawSections)
  const summary = [
    pluralize(sections.length, 'area'),
    pluralize(lines.length, 'line'),
    pluralize(dimensions.length, 'dimension'),
    pluralize(annotations.length, 'annotation')
  ].join(' / ')

  return (
    <section className={wrapperClassName} aria-label={artifact.title}>
      <div className="border-border flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3">
        <div className="min-w-0">
          <h3 className="truncate text-sm font-semibold">{artifact.title}</h3>
          <p className="text-muted-foreground text-xs">Blueprint schematic</p>
        </div>
        <span className="border-border text-muted-foreground rounded-full border px-2.5 py-1 text-xs">
          {summary}
        </span>
      </div>

      <div className="max-h-[680px] overflow-auto bg-slate-50">
        <svg
          className="block"
          style={{
            width: Math.max(bounds.width, 720),
            height: Math.max(bounds.height, 420),
            minWidth: '100%'
          }}
          role="img"
          aria-label={`${artifact.title} blueprint`}
          viewBox={`${bounds.minX} ${bounds.minY} ${bounds.width} ${bounds.height}`}
          preserveAspectRatio="xMinYMin meet"
        >
          <defs>
            <pattern id={gridId} width="32" height="32" patternUnits="userSpaceOnUse">
              <path d="M 32 0 L 0 0 0 32" fill="none" stroke="#dbe4ee" strokeWidth="1" />
            </pattern>
            <marker
              id={dimensionMarkerId}
              markerHeight="8"
              markerWidth="8"
              orient="auto-start-reverse"
              refX="4"
              refY="4"
            >
              <path d="M 8 0 L 0 4 L 8 8" fill="none" stroke={mutedInk} strokeWidth="1.5" />
            </marker>
          </defs>

          <rect
            x={bounds.minX}
            y={bounds.minY}
            width={bounds.width}
            height={bounds.height}
            fill={backgroundInk}
          />
          <rect
            x={bounds.minX}
            y={bounds.minY}
            width={bounds.width}
            height={bounds.height}
            fill={`url(#${gridId})`}
          />
          <rect
            x={bounds.minX + canvasPadding / 2}
            y={bounds.minY + canvasPadding / 2}
            width={Math.max(bounds.width - canvasPadding, 1)}
            height={Math.max(bounds.height - canvasPadding, 1)}
            fill="none"
            stroke="#cbd5e1"
            strokeDasharray="8 10"
            strokeWidth="1.5"
          />

          {sections.map((section, index) => (
            <BlueprintSectionShape
              key={section.id}
              coordinateScale={coordinateScale}
              index={index}
              section={section}
              unit={unit}
            />
          ))}

          {lines.map((line) => (
            <BlueprintLineShape key={line.id} line={line} />
          ))}

          {dimensions.map((dimension, index) => (
            <BlueprintDimensionShape
              key={dimension.id}
              dimension={dimension}
              coordinateScale={coordinateScale}
              index={index}
              markerId={dimensionMarkerId}
              unit={unit}
            />
          ))}

          {positionedAnnotations.map((annotation) => (
            <BlueprintAnnotationShape key={annotation.id} annotation={annotation} />
          ))}

          <BlueprintScaleBar
            bounds={bounds}
            coordinateScale={coordinateScale}
            scale={artifact.data.scale}
            unit={unit}
          />
        </svg>
      </div>

      <BlueprintFooter
        bounds={bounds}
        coordinateScale={coordinateScale}
        notes={notes}
        scale={artifact.data.scale}
        unit={unit}
      />
    </section>
  )
}

export { BlueprintViewer }

// --------------

function BlueprintSectionShape({
  coordinateScale,
  index,
  section,
  unit
}: {
  coordinateScale: number
  index: number
  section: BlueprintSection
  unit?: string
}) {
  const center = getSectionCenter(section)
  const isPolygon = hasPolygon(section)
  const isRectangle = hasRectangle(section)
  const fill = getSafePaint(section.fill, getSectionFill(section, index))
  const stroke = getSafePaint(section.stroke, '#94a3b8')
  const labelSize = getSectionLabelSize(section)
  const dimensionsLabel = getSectionDimensionsLabel(section, unit, coordinateScale)
  const labelLines = wrapText(section.label, 24, 2)

  if (!center || (!isPolygon && !isRectangle)) {
    return null
  }

  return (
    <g>
      {isPolygon ? (
        <polygon
          points={section.points.map((point) => `${point.x},${point.y}`).join(' ')}
          fill={fill}
          stroke={stroke}
          strokeWidth="2"
        />
      ) : (
        <rect
          x={section.x}
          y={section.y}
          width={section.width}
          height={section.height}
          rx="4"
          fill={fill}
          stroke={stroke}
          strokeWidth="2"
        />
      )}

      <text
        x={center.x}
        y={dimensionsLabel ? center.y - labelSize * 0.85 : center.y - (labelLines.length - 1) * 6}
        fill={blueprintInk}
        fontSize={labelSize}
        fontWeight="700"
        paintOrder="stroke"
        stroke="#f8fafc"
        strokeWidth="4"
        textAnchor="middle"
        dominantBaseline="middle"
      >
        {labelLines.map((line, lineIndex) => (
          <tspan key={`${section.id}-label-${lineIndex}`} x={center.x} dy={lineIndex === 0 ? 0 : 14}>
            {line}
          </tspan>
        ))}
      </text>
      {dimensionsLabel && (
        <text
          x={center.x}
          y={center.y + labelSize * 0.9}
          fill={mutedInk}
          fontSize={Math.max(labelSize - 3, 9)}
          fontWeight="600"
          textAnchor="middle"
          dominantBaseline="middle"
        >
          {dimensionsLabel}
        </text>
      )}
    </g>
  )
}

function BlueprintLineShape({ line }: { line: BlueprintLine }) {
  const endpoints = getLineEndpoints(line)

  if (!endpoints) {
    return null
  }

  const kind = line.kind?.toLowerCase() ?? ''
  const isOpening = kind.includes('door') || kind.includes('window') || kind.includes('opening')
  const strokeWidth = clamp(line.thickness ?? (kind.includes('exterior') ? 8 : 4), 2, 16)
  const midpoint = getMidpoint(endpoints.start, endpoints.end)

  return (
    <g>
      <line
        x1={endpoints.start.x}
        y1={endpoints.start.y}
        x2={endpoints.end.x}
        y2={endpoints.end.y}
        stroke={isOpening ? '#2563eb' : blueprintInk}
        strokeDasharray={isOpening ? '10 8' : undefined}
        strokeLinecap="square"
        strokeWidth={strokeWidth}
      />
      {line.label && (
        <text
          x={midpoint.x}
          y={midpoint.y - 10}
          fill={mutedInk}
          fontSize="11"
          fontWeight="700"
          textAnchor="middle"
        >
          {truncateText(line.label, 22)}
        </text>
      )}
    </g>
  )
}

function BlueprintDimensionShape({
  coordinateScale,
  dimension,
  index,
  markerId,
  unit
}: {
  coordinateScale: number
  dimension: BlueprintDimension
  index: number
  markerId: string
  unit?: string
}) {
  const geometry = getDimensionGeometry(dimension, index)

  if (!geometry) {
    return null
  }

  const label =
    dimension.label ??
    `${Math.round(getDistance(geometry.start, geometry.end) / coordinateScale)}${
      unit ? ` ${unit}` : ''
    }`
  const textAngle =
    geometry.angle > 90 || geometry.angle < -90 ? geometry.angle + 180 : geometry.angle

  return (
    <g>
      <line
        x1={geometry.start.x}
        y1={geometry.start.y}
        x2={geometry.dimensionStart.x}
        y2={geometry.dimensionStart.y}
        stroke={mutedInk}
        strokeWidth="1"
      />
      <line
        x1={geometry.end.x}
        y1={geometry.end.y}
        x2={geometry.dimensionEnd.x}
        y2={geometry.dimensionEnd.y}
        stroke={mutedInk}
        strokeWidth="1"
      />
      <line
        x1={geometry.dimensionStart.x}
        y1={geometry.dimensionStart.y}
        x2={geometry.dimensionEnd.x}
        y2={geometry.dimensionEnd.y}
        markerEnd={`url(#${markerId})`}
        markerStart={`url(#${markerId})`}
        stroke={mutedInk}
        strokeWidth="1.5"
      />
      <text
        x={geometry.labelPoint.x}
        y={geometry.labelPoint.y - 7}
        fill={blueprintInk}
        fontSize="11"
        fontWeight="700"
        textAnchor="middle"
        transform={`rotate(${textAngle} ${geometry.labelPoint.x} ${geometry.labelPoint.y - 7})`}
      >
        {truncateText(label, 24)}
      </text>
    </g>
  )
}

function BlueprintAnnotationShape({
  annotation
}: {
  annotation: PositionedBlueprintAnnotation
}) {
  const connectorX =
    annotation.labelX > annotation.targetPoint.x
      ? annotation.labelX
      : annotation.labelX + annotation.labelWidth
  const connectorY = clamp(
    annotation.targetPoint.y,
    annotation.labelY + 10,
    annotation.labelY + annotation.labelHeight - 10
  )

  return (
    <g>
      <line
        x1={annotation.targetPoint.x}
        y1={annotation.targetPoint.y}
        x2={connectorX}
        y2={connectorY}
        stroke="#0f766e"
        strokeDasharray="4 6"
        strokeWidth="1.5"
      />
      <circle cx={annotation.targetPoint.x} cy={annotation.targetPoint.y} r="5" fill="#0f766e" />
      <g transform={`translate(${annotation.labelX} ${annotation.labelY})`}>
        <rect
          width={annotation.labelWidth}
          height={annotation.labelHeight}
          rx="6"
          fill="#ffffff"
          stroke="#0f766e"
          strokeWidth="1.5"
        />
        <text x="12" y="18" fill={blueprintInk} fontSize="12" fontWeight="700">
          {annotation.labelLines.map((line, index) => (
            <tspan key={`${annotation.id}-label-${index}`} x="12" dy={index === 0 ? 0 : 14}>
              {line}
            </tspan>
          ))}
        </text>
        {annotation.noteLine && (
          <text
            x="12"
            y={annotation.labelLines.length > 1 ? 48 : 36}
            fill={mutedInk}
            fontSize="10"
            fontWeight="600"
          >
            {annotation.noteLine}
          </text>
        )}
      </g>
    </g>
  )
}

function BlueprintScaleBar({
  bounds,
  coordinateScale,
  scale,
  unit
}: {
  bounds: CanvasBounds
  coordinateScale: number
  scale?: BlueprintScale | string
  unit?: string
}) {
  const scaleLength = clamp(
    typeof scale === 'object' && scale.length ? scale.length : bounds.width * 0.12,
    72,
    180
  )
  const x = bounds.minX + canvasPadding * 0.72
  const y = bounds.maxY - canvasPadding * 0.58
  const label = getScaleLabel(scale, unit, scaleLength / coordinateScale)

  return (
    <g>
      <line x1={x} y1={y} x2={x + scaleLength} y2={y} stroke={blueprintInk} strokeWidth="3" />
      <line x1={x} y1={y - 9} x2={x} y2={y + 9} stroke={blueprintInk} strokeWidth="3" />
      <line
        x1={x + scaleLength}
        y1={y - 9}
        x2={x + scaleLength}
        y2={y + 9}
        stroke={blueprintInk}
        strokeWidth="3"
      />
      <text x={x} y={y + 27} fill={mutedInk} fontSize="11" fontWeight="700">
        {label}
      </text>
    </g>
  )
}

function BlueprintFooter({
  bounds,
  coordinateScale,
  notes,
  scale,
  unit
}: {
  bounds: CanvasBounds
  coordinateScale: number
  notes: string[]
  scale?: BlueprintScale | string
  unit?: string
}) {
  return (
    <div className="border-border bg-background text-muted-foreground grid gap-3 border-t px-4 py-3 text-xs md:grid-cols-[1fr_auto]">
      <div className="min-w-0">
        {notes.length > 0 ? (
          <ul className="grid gap-1">
            {notes.slice(0, 3).map((note) => (
              <li key={note} className="truncate">
                {note}
              </li>
            ))}
          </ul>
        ) : (
          <span>No blueprint notes provided.</span>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2 md:justify-end">
        <span className="border-border rounded-full border px-2.5 py-1">
          {getScaleLabel(scale, unit, (bounds.width * 0.12) / coordinateScale)}
        </span>
        <span className="border-border rounded-full border px-2.5 py-1">
          {Math.round(bounds.width / coordinateScale)} x {Math.round(bounds.height / coordinateScale)}
        </span>
      </div>
    </div>
  )
}

function BlueprintState({ title, message }: { title: string; message: string }) {
  return (
    <div className="flex min-h-[320px] items-center justify-center px-6 py-12 text-center">
      <div className="max-w-sm">
        <p className="text-sm font-semibold">{title}</p>
        <p className="text-muted-foreground mt-1 text-sm">{message}</p>
      </div>
    </div>
  )
}

// --------------

function getBlueprintSections(data: BlueprintData) {
  return [
    ...getElementSections(data.elements),
    ...(data.rooms ?? []),
    ...(data.sections ?? [])
  ].filter(isRenderableSection)
}

function getBlueprintLines(data: BlueprintData) {
  return [...getElementLines(data.elements), ...(data.walls ?? []), ...(data.lines ?? [])].filter((line) =>
    Boolean(getLineEndpoints(line))
  )
}

function getBlueprintDimensions(data: BlueprintData) {
  return [...getElementDimensions(data.elements, data.unit), ...(data.dimensions ?? [])]
}

function getBlueprintAnnotations(data: BlueprintData) {
  return [...getElementAnnotations(data.elements, data.unit), ...(data.annotations ?? [])]
}

function getElementSections(elements: BlueprintElement[] | undefined): BlueprintSection[] {
  return (elements ?? [])
    .filter((element) => element.kind === 'area' && element.points.length >= 3)
    .map((element) => ({
      id: element.id,
      label: element.label ?? element.id,
      kind: element.kind,
      points: element.points,
      notes: getElementMeasurementNote(element, undefined)
    }))
}

function getElementLines(elements: BlueprintElement[] | undefined): BlueprintLine[] {
  return (elements ?? [])
    .filter((element) =>
      ['wall', 'door', 'window'].includes(element.kind) && element.points.length >= 2
    )
    .map((element) => ({
      id: element.id,
      label: element.label,
      kind: element.kind,
      from: element.points[0],
      to: element.points[1],
      thickness: element.kind === 'wall' ? 5 : 3
    }))
}

function getElementDimensions(
  elements: BlueprintElement[] | undefined,
  unit: string | undefined
): BlueprintDimension[] {
  return (elements ?? [])
    .filter((element) => element.kind === 'dimension' && element.points.length >= 2)
    .map((element) => ({
      id: element.id,
      label: element.label ?? getMeasurementLabel(element.measurement, unit),
      from: element.points[0],
      to: element.points[1]
    }))
}

function getElementAnnotations(
  elements: BlueprintElement[] | undefined,
  unit: string | undefined
): BlueprintAnnotation[] {
  return (elements ?? [])
    .filter(
      (element) =>
        (element.kind === 'fixture' || element.kind === 'label') && element.points.length >= 1
    )
    .map((element) => ({
      id: element.id,
      label: element.label ?? element.id,
      x: element.points[0].x,
      y: element.points[0].y,
      note: getElementMeasurementNote(element, unit)
    }))
}

function getBlueprintDisplayScale(
  data: BlueprintData,
  sections: BlueprintSection[],
  lines: BlueprintLine[],
  dimensions: BlueprintDimension[],
  annotations: BlueprintAnnotation[]
) {
  const points: BlueprintPoint[] = []

  if (data.bounds) {
    points.push(
      { x: 0, y: 0 },
      { x: data.bounds.width, y: data.bounds.height }
    )
  }

  sections.forEach((section) => {
    points.push(...getSectionPoints(section))
  })

  lines.forEach((line) => {
    const endpoints = getLineEndpoints(line)

    if (endpoints) {
      points.push(endpoints.start, endpoints.end)
    }
  })

  dimensions.forEach((dimension) => {
    const endpoints = getLineEndpoints(dimension)

    if (endpoints) {
      points.push(endpoints.start, endpoints.end)
    }
  })

  annotations.forEach((annotation) => {
    points.push(
      { x: annotation.x, y: annotation.y },
      annotation.target ?? { x: annotation.x, y: annotation.y }
    )
  })

  if (points.length === 0) {
    return 1
  }

  const minX = Math.min(...points.map((point) => point.x))
  const maxX = Math.max(...points.map((point) => point.x))
  const minY = Math.min(...points.map((point) => point.y))
  const maxY = Math.max(...points.map((point) => point.y))
  const maxSpan = Math.max(maxX - minX, maxY - minY)

  return maxSpan > 0 && maxSpan < 220 ? clamp(520 / maxSpan, 1, 48) : 1
}

function scaleBlueprintData(data: BlueprintData, scale: number): BlueprintData {
  if (scale === 1 || !data.bounds) {
    return data
  }

  return {
    ...data,
    bounds: {
      width: data.bounds.width * scale,
      height: data.bounds.height * scale
    }
  }
}

function scaleBlueprintSections(sections: BlueprintSection[], scale: number) {
  if (scale === 1) {
    return sections
  }

  return sections.map((section) => ({
    ...section,
    x: scaleOptionalNumber(section.x, scale),
    y: scaleOptionalNumber(section.y, scale),
    width: scaleOptionalNumber(section.width, scale),
    height: scaleOptionalNumber(section.height, scale),
    points: section.points?.map((point) => scalePoint(point, scale))
  }))
}

function scaleBlueprintLines(lines: BlueprintLine[], scale: number) {
  if (scale === 1) {
    return lines
  }

  return lines.map((line) => ({
    ...line,
    from: scaleOptionalPoint(line.from, scale),
    to: scaleOptionalPoint(line.to, scale),
    start: scaleOptionalPoint(line.start, scale),
    end: scaleOptionalPoint(line.end, scale),
    thickness: line.thickness ? line.thickness * Math.sqrt(scale) : line.thickness
  }))
}

function scaleBlueprintDimensions(dimensions: BlueprintDimension[], scale: number) {
  if (scale === 1) {
    return dimensions
  }

  return dimensions.map((dimension) => ({
    ...dimension,
    from: scaleOptionalPoint(dimension.from, scale),
    to: scaleOptionalPoint(dimension.to, scale),
    start: scaleOptionalPoint(dimension.start, scale),
    end: scaleOptionalPoint(dimension.end, scale),
    offset: dimension.offset ? dimension.offset * scale : dimension.offset
  }))
}

function scaleBlueprintAnnotations(annotations: BlueprintAnnotation[], scale: number) {
  if (scale === 1) {
    return annotations
  }

  return annotations.map((annotation) => ({
    ...annotation,
    x: annotation.x * scale,
    y: annotation.y * scale,
    target: scaleOptionalPoint(annotation.target, scale)
  }))
}

function scaleOptionalPoint(point: BlueprintPoint | undefined, scale: number) {
  return point ? scalePoint(point, scale) : undefined
}

function scalePoint(point: BlueprintPoint, scale: number): BlueprintPoint {
  return {
    x: point.x * scale,
    y: point.y * scale
  }
}

function scaleOptionalNumber(value: number | undefined, scale: number) {
  return value === undefined ? undefined : value * scale
}

function positionBlueprintAnnotations(
  annotations: BlueprintAnnotation[],
  bounds: CanvasBounds
): PositionedBlueprintAnnotation[] {
  const positioned: PositionedBlueprintAnnotation[] = []
  const sortedAnnotations = [...annotations].sort((a, b) => {
    const aTarget = getAnnotationTarget(a)
    const bTarget = getAnnotationTarget(b)

    return aTarget.y - bTarget.y
  })

  sortedAnnotations.forEach((annotation) => {
    const targetPoint = getAnnotationTarget(annotation)
    const labelLines = wrapText(annotation.label, 24, 2)
    const noteLine = annotation.note ? truncateText(annotation.note, 34) : undefined
    const labelWidth = clamp(
      Math.max(
        ...labelLines.map((line) => line.length * 7 + 28),
        noteLine ? noteLine.length * 5.4 + 28 : 0,
        132
      ),
      132,
      220
    )
    const labelHeight = 24 + labelLines.length * 14 + (noteLine ? 16 : 0)
    const placeRight = targetPoint.x < bounds.minX + bounds.width * 0.58
    const proposedX = placeRight
      ? targetPoint.x + annotationGap
      : targetPoint.x - labelWidth - annotationGap
    const labelX = clamp(proposedX, bounds.minX + 16, bounds.maxX - labelWidth - 16)
    let labelY = clamp(
      targetPoint.y - labelHeight / 2,
      bounds.minY + 16,
      bounds.maxY - labelHeight - 16
    )
    let attempts = 0

    while (
      positioned.some((item) =>
        boxesOverlap(
          { x: labelX, y: labelY, width: labelWidth, height: labelHeight },
          {
            x: item.labelX,
            y: item.labelY,
            width: item.labelWidth,
            height: item.labelHeight
          }
        )
      ) &&
      attempts < positioned.length + 1
    ) {
      labelY = clamp(labelY + labelHeight + 10, bounds.minY + 16, bounds.maxY - labelHeight - 16)
      attempts += 1
    }

    positioned.push({
      ...annotation,
      targetPoint,
      labelX,
      labelY,
      labelWidth,
      labelHeight,
      labelLines,
      noteLine
    })
  })

  return positioned.sort(
    (a, b) =>
      annotations.findIndex((item) => item.id === a.id) -
      annotations.findIndex((item) => item.id === b.id)
  )
}

function getAnnotationTarget(annotation: BlueprintAnnotation): BlueprintPoint {
  return annotation.target ?? {
    x: annotation.x,
    y: annotation.y
  }
}

function boxesOverlap(
  a: { x: number; y: number; width: number; height: number },
  b: { x: number; y: number; width: number; height: number }
) {
  return !(
    a.x + a.width + 8 < b.x ||
    b.x + b.width + 8 < a.x ||
    a.y + a.height + 8 < b.y ||
    b.y + b.height + 8 < a.y
  )
}

function getCanvasBounds(
  data: BlueprintData,
  sections: BlueprintSection[],
  lines: BlueprintLine[],
  dimensions: BlueprintDimension[],
  annotations: BlueprintAnnotation[]
): CanvasBounds {
  const points: BlueprintPoint[] = [
    {
      x: 0,
      y: 0
    },
    {
      x: data.bounds?.width ?? defaultBounds.width,
      y: data.bounds?.height ?? defaultBounds.height
    }
  ]

  sections.forEach((section) => {
    points.push(...getSectionPoints(section))
  })

  lines.forEach((line) => {
    const endpoints = getLineEndpoints(line)

    if (!endpoints) return

    points.push(endpoints.start, endpoints.end)
  })

  dimensions.forEach((dimension, index) => {
    const geometry = getDimensionGeometry(dimension, index)

    if (!geometry) return

    points.push(
      geometry.start,
      geometry.end,
      geometry.dimensionStart,
      geometry.dimensionEnd,
      geometry.labelPoint
    )
  })

  annotations.forEach((annotation) => {
    points.push(
      {
        x: annotation.x,
        y: annotation.y
      },
      annotation.target ?? {
        x: annotation.x,
        y: annotation.y
      }
    )
  })

  const minX = Math.min(...points.map((point) => point.x)) - canvasPadding
  const minY = Math.min(...points.map((point) => point.y)) - canvasPadding
  const maxX = Math.max(...points.map((point) => point.x)) + canvasPadding
  const maxY = Math.max(...points.map((point) => point.y)) + canvasPadding

  return {
    minX,
    minY,
    maxX,
    maxY,
    width: Math.max(maxX - minX, 1),
    height: Math.max(maxY - minY, 1)
  }
}

function isRenderableSection(section: BlueprintSection) {
  return hasPolygon(section) || hasRectangle(section)
}

function hasPolygon(section: BlueprintSection): section is BlueprintSection & {
  points: BlueprintPoint[]
} {
  return Array.isArray(section.points) && section.points.length >= 3
}

function hasRectangle(section: BlueprintSection): section is BlueprintSection & {
  x: number
  y: number
  width: number
  height: number
} {
  return (
    isFiniteNumber(section.x) &&
    isFiniteNumber(section.y) &&
    isPositiveNumber(section.width) &&
    isPositiveNumber(section.height)
  )
}

function getSectionPoints(section: BlueprintSection) {
  if (hasPolygon(section)) {
    return section.points
  }

  if (!hasRectangle(section)) {
    return []
  }

  return [
    {
      x: section.x,
      y: section.y
    },
    {
      x: section.x + section.width,
      y: section.y
    },
    {
      x: section.x + section.width,
      y: section.y + section.height
    },
    {
      x: section.x,
      y: section.y + section.height
    }
  ]
}

function getSectionCenter(section: BlueprintSection): BlueprintPoint | null {
  const points = getSectionPoints(section)

  if (points.length === 0) {
    return null
  }

  return {
    x: points.reduce((sum, point) => sum + point.x, 0) / points.length,
    y: points.reduce((sum, point) => sum + point.y, 0) / points.length
  }
}

function getSectionLabelSize(section: BlueprintSection) {
  if (!hasRectangle(section)) {
    return 13
  }

  return clamp(Math.min(section.width, section.height) * 0.14, 10, 15)
}

function getSectionDimensionsLabel(
  section: BlueprintSection,
  unit: string | undefined,
  coordinateScale: number
) {
  if (!hasRectangle(section) || section.width < 84 || section.height < 52) {
    return null
  }

  const suffix = unit ? ` ${unit}` : ''
  return `${Math.round(section.width / coordinateScale)} x ${Math.round(
    section.height / coordinateScale
  )}${suffix}`
}

function getSectionFill(section: BlueprintSection, index: number) {
  const kind = section.kind?.toLowerCase() ?? ''

  if (kind.includes('utility') || kind.includes('service')) return '#e0f2fe'
  if (kind.includes('private') || kind.includes('office')) return '#ecfdf5'
  if (kind.includes('circulation') || kind.includes('hall')) return '#f1f5f9'
  if (kind.includes('storage')) return '#fef3c7'

  return areaPalette[index % areaPalette.length]
}

function getLineEndpoints(line: BlueprintLine | BlueprintDimension): LineEndpoints | null {
  const start = line.from ?? line.start
  const end = line.to ?? line.end

  if (!isPoint(start) || !isPoint(end)) {
    return null
  }

  return {
    start,
    end
  }
}

function getDimensionGeometry(
  dimension: BlueprintDimension,
  index: number
): DimensionGeometry | null {
  const endpoints = getLineEndpoints(dimension)

  if (!endpoints) {
    return null
  }

  const distance = getDistance(endpoints.start, endpoints.end)

  if (distance === 0) {
    return null
  }

  const offset = dimension.offset ?? 28 + index * 10
  const dx = endpoints.end.x - endpoints.start.x
  const dy = endpoints.end.y - endpoints.start.y
  const normal = {
    x: -dy / distance,
    y: dx / distance
  }
  const dimensionStart = movePoint(endpoints.start, normal, offset)
  const dimensionEnd = movePoint(endpoints.end, normal, offset)

  return {
    start: endpoints.start,
    end: endpoints.end,
    dimensionStart,
    dimensionEnd,
    labelPoint: getMidpoint(dimensionStart, dimensionEnd),
    angle: Math.atan2(dy, dx) * (180 / Math.PI)
  }
}

function getBlueprintNotes(data: BlueprintData, sections: BlueprintSection[]) {
  const dataNotes = Array.isArray(data.notes)
    ? data.notes
    : typeof data.notes === 'string'
      ? [data.notes]
      : []
  const sourceNotes = data.sourceNotes ?? []
  const sectionNotes = sections
    .map((section) => section.notes)
    .filter((note): note is string => Boolean(note))
  const confidenceNote =
    data.confidence === undefined ? [] : [`Confidence ${Math.round(data.confidence * 100)}%`]

  return [...dataNotes, ...sourceNotes, ...sectionNotes, ...confidenceNote]
}

function getElementMeasurementNote(element: BlueprintElement, unit: string | undefined) {
  return element.measurement === undefined ? undefined : getMeasurementLabel(element.measurement, unit)
}

function getMeasurementLabel(value: number | undefined, unit: string | undefined) {
  if (value === undefined) return undefined
  return `${value}${unit ? ` ${unit}` : ''}`
}

function getScaleLabel(
  scale: BlueprintScale | string | undefined,
  unit: string | undefined,
  fallbackLength: number
) {
  if (typeof scale === 'string' && scale.trim()) {
    return scale
  }

  if (typeof scale === 'object') {
    if (scale.label) return scale.label
    if (scale.ratio) return scale.ratio
  }

  return `${Math.round(fallbackLength)}${unit ? ` ${unit}` : ''}`
}

function getScaleUnit(scale: BlueprintScale | string | undefined) {
  return typeof scale === 'object' ? scale.unit : undefined
}

function getSafePaint(value: string | undefined, fallback: string) {
  if (!value) {
    return fallback
  }

  return /^#[0-9a-f]{3,8}$/i.test(value) ? value : fallback
}

function isPoint(value: BlueprintPoint | undefined): value is BlueprintPoint {
  return isFiniteNumber(value?.x) && isFiniteNumber(value?.y)
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value)
}

function isPositiveNumber(value: unknown): value is number {
  return isFiniteNumber(value) && value > 0
}

function movePoint(point: BlueprintPoint, normal: BlueprintPoint, offset: number): BlueprintPoint {
  return {
    x: point.x + normal.x * offset,
    y: point.y + normal.y * offset
  }
}

function getMidpoint(start: BlueprintPoint, end: BlueprintPoint): BlueprintPoint {
  return {
    x: (start.x + end.x) / 2,
    y: (start.y + end.y) / 2
  }
}

function getDistance(start: BlueprintPoint, end: BlueprintPoint) {
  return Math.hypot(end.x - start.x, end.y - start.y)
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max)
}

function truncateText(value: string, maxLength: number) {
  if (value.length <= maxLength) return value
  return `${value.slice(0, Math.max(maxLength - 3, 0))}...`
}

function wrapText(value: string, maxLineLength: number, maxLines: number) {
  const words = value.split(/\s+/).filter(Boolean)
  const lines: string[] = []

  words.forEach((word) => {
    const currentLine = lines[lines.length - 1]

    if (!currentLine || `${currentLine} ${word}`.length > maxLineLength) {
      lines.push(truncateText(word, maxLineLength))
      return
    }

    lines[lines.length - 1] = `${currentLine} ${word}`
  })

  if (lines.length <= maxLines) {
    return lines
  }

  return [
    ...lines.slice(0, maxLines - 1),
    truncateText(lines.slice(maxLines - 1).join(' '), maxLineLength)
  ]
}

function pluralize(count: number, label: string) {
  return `${count} ${count === 1 ? label : `${label}s`}`
}

function joinClassNames(...classNames: Array<string | undefined>) {
  return classNames.filter(Boolean).join(' ')
}
